/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import { searchStates } from "../search/SearchConstants";
import { docViewModes } from "constants/enums";
import { trackSearchOpened } from "analytics/events";
import { createDeferredPromiseObject } from "utils/DeferredPromise";
import { getSearchManager } from "selectors/searchSelectors";
import { isFullScreenOrFitOnePageMode } from "selectors/documentSelectors";

class SearchStore {
  @observable showSearchInputUI;
  @observable showSearchProgessBar;
  @observable showNumberOfSearchResultsFound;
  @observable pageBeingSearched;
  @observable numberOfSearchResultsInDocument;
  @observable indexOfCurrentSearchResult;
  @observable disablePreviousSearchButton;
  @observable disableNextSearchButton;
  @observable searchState;
  @observable defaultSearchQuery;
  @observable searchInputClassName;
  @observable searchWidgetClassName;
  @observable searchInputStyle;
  @observable searchWidgetStyle;
  @observable shouldshowSearchInHud;
  @observable firstSearchResultPromiseObj;
  @observable activeSearchAPIObj;
  // keeps tracks of how many search wrappers are mounted in the dom
  // needed to ensure that we have only search input component mounted in the
  // at any point in time
  @observable numSearchWrapperComponentMounted;
  searchAddOnPanel = null;

  constructor() {
    this.init();
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  init(switchPreview = false) {
    this.showSearchInputUI = false;
    this.showSearchProgessBar = false;
    this.showNumberOfSearchResultsFound = false;
    this.pageBeingSearched = 0;
    this.numberOfSearchResultsInDocument = 0;
    this.indexOfCurrentSearchResult = 0;
    this.disablePreviousSearchButton = false;
    this.disableNextSearchButton = false;
    this.searchState = searchStates.NO_ACTIVE_SEARCH;
    this.defaultSearchQuery = "";
    if (!switchPreview) {
      this.searchInputClassName = "";
      this.searchWidgetClassName = "";
      this.searchInputStyle = {};
      this.searchWidgetStyle = {};
    }
    this.shouldshowSearchInHud = false;
    this.numSearchWrapperComponentMounted = 0;
    this.firstSearchResultPromiseObj = null;
    this.activeSearchAPIObj = null;
    // non-observables
    this.customCss = {
      padding: "5px"
    };
    this.useFullWidthOnTablet = true;
    this.searchQuery = "";
    this.searchAddOnPanel = null;
  }

  setUseFullWidthOnTablet(value) {
    this.useFullWidthOnTablet = value;
  }

  setCustomCss(value) {
    this.customCss = value;
  }

  setSearchQuery(value) {
    this.searchQuery = value;
  }

  @action
  searchWrapperMounted() {
    this.numSearchWrapperComponentMounted += 1;
  }
  @action
  searchWrapperUnmounted() {
    if (this.numSearchWrapperComponentMounted > 0) {
      this.numSearchWrapperComponentMounted -= 1;
    }
  }

  @action
  setShowSearchInputUI(showSearchInputUI = false, viewMode) {
    if (showSearchInputUI && !this.showSearchInputUI) {
      // if not already opened, record that we opened search
      if (
        viewMode === docViewModes.CLASSIC_VIEW ||
        viewMode === docViewModes.FIT_ONE_FULL_PAGE ||
        viewMode === docViewModes.FULL_SCREEN
      ) {
        trackSearchOpened();
      }
    }
    this.showSearchInputUI = showSearchInputUI;
  }

  @action
  setShowSearchProgessBar(showSearchProgessBar = false) {
    this.showSearchProgessBar = showSearchProgessBar;
  }

  @action
  setShowNumberOfSearchResultsFound(showNumberOfSearchResultsFound = false) {
    this.showNumberOfSearchResultsFound = showNumberOfSearchResultsFound;
  }

  @action
  setPageBeingSearched(pageBeingSearched = 0) {
    this.pageBeingSearched = pageBeingSearched;
  }

  @action
  setNumberOfSearchResultsInDocument(numberOfSearchResultsInDocument = 0) {
    this.numberOfSearchResultsInDocument = numberOfSearchResultsInDocument;
  }

  @action
  setIndexOfCurrentSearchResult(indexOfCurrentSearchResult = 0) {
    this.indexOfCurrentSearchResult = indexOfCurrentSearchResult;
  }

  @action
  setDisablePrevious(disablePreviousSearchButton = false) {
    this.disablePreviousSearchButton = disablePreviousSearchButton;
  }

  @action
  setDisableNext(disableNextSearchButton = false) {
    this.disableNextSearchButton = disableNextSearchButton;
  }

  @action
  setSearchState(searchState = searchStates.NO_ACTIVE_SEARCH) {
    this.searchState = searchState;
  }

  @action
  setDefaultSearchQuery(defaultSearchQuery = "") {
    this.defaultSearchQuery = defaultSearchQuery;
  }

  @action
  setSearchInputClassName(searchInputClassName = "") {
    this.searchInputClassName = searchInputClassName;
  }

  @action
  setSearchWidgetClassName(searchWidgetClassName = "") {
    this.searchWidgetClassName = searchWidgetClassName;
  }

  @action
  setSearchInputStyle(searchInputStyle = {}) {
    this.searchInputStyle = searchInputStyle;
  }

  @action
  setSearchWidgetStyle(searchWidgetStyle = {}) {
    this.searchWidgetStyle = searchWidgetStyle;
  }

  @action
  setShouldshowSearchInHud(shouldshowSearchInHud = false) {
    this.shouldshowSearchInHud = shouldshowSearchInHud;
  }

  @action
  setNewFirstSearchResultPromise() {
    this.firstSearchResultPromiseObj = createDeferredPromiseObject();
  }

  @action
  setActiveSearchAPIObj(searchAPIobj = null) {
    this.activeSearchAPIObj = searchAPIobj;
  }

  sendOnResultsUpdate(stores) {
    if (this.activeSearchAPIObj && this.activeSearchAPIObj.getUpdateCb()) {
      let searchManager = getSearchManager(stores);
      if (searchManager) {
        let searchResult = searchManager.searchResultsIterator.getCurrent().searchResult;
        this.activeSearchAPIObj.getUpdateCb().trigger("searchresultsupdate", {
          currResult: {
            pageNum: searchResult.pageIndex + 1,
            index: searchResult.searchResultIndex
          },
          totalResults: this.numberOfSearchResultsInDocument,
          status: this.searchState === searchStates.SEARCH_COMPLETE ? "COMPLETE" : "IN_PROGRESS"
        });
      }
    }
  }

  registerSearchAddOnPanel(args) {
    this.searchAddOnPanel = args;
  }
}

export { SearchStore };
